import { makeStyles } from '@material-ui/core';

const corporateBlue = '#20a0ff';
const corporateNavy = '#000066';
const corporateCyan = '#CCFFFF';
const corporateBorder = '#8cd2ec';

const uninitializedStyle = { backgroundColor: '#fff1c2', color: '#7a5200' };
const pendingStyle = { backgroundColor: '#fff4d6', color: '#835900' };
const successStyle = { backgroundColor: '#e2f3e8', color: '#21683a' };
const failureStyle = { backgroundColor: '#fde8e7', color: '#9f2924' };

const useStyles = makeStyles(theme => ({
	dialogPaper: {
		width: '94vw',
		maxWidth: 1400,
		maxHeight: '92vh',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		boxShadow: theme.shadows[12],
	},
	darkDialogPaper: {
		backgroundColor: '#111b24',
		color: '#edf3f7',
		'& $header': {
			backgroundColor: '#17242e',
			borderColor: '#354754',
		},
		'& $headerTitle, & $headerSummary, & $headerSummary .MuiTypography-root, & $amount': {
			color: '#edf3f7',
		},
		'& $headerSummary .MuiTypography-colorTextSecondary, & .MuiTypography-colorTextSecondary': {
			color: '#adbdc9',
		},
		'& $content': {
			backgroundColor: '#111b24',
		},
		'& $footer': {
			backgroundColor: '#17242e',
			borderColor: '#354754',
		},
		'& $card': {
			backgroundColor: '#182630',
			borderColor: '#405561',
			color: '#edf3f7',
		},
		'& $cardHeader': {
			backgroundColor: '#263a46',
			color: '#eaf2f6',
		},
		'& $field': {
			borderColor: '#344955',
		},
		'& $fieldLabel': {
			color: '#adbdc9',
		},
		'& $fieldText': {
			color: '#edf3f7',
		},
		'& $accordion': {
			backgroundColor: '#182630',
			borderColor: '#405561',
			color: '#edf3f7',
		},
		'& $accordionSummary': {
			color: '#edf3f7',
			'& .MuiAccordionSummary-expandIcon': {
				color: '#adbdc9',
			},
		},
		'& $accordionDetails': {
			backgroundColor: '#182630',
			color: '#edf3f7',
		},
		'& $code': {
			backgroundColor: '#0b1319',
			color: '#e5edf2',
		},
		'& $copyButton, & .MuiIconButton-root': {
			color: corporateBlue,
		},
	},
	header: {
		position: 'sticky',
		top: 0,
		zIndex: 2,
		background: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(2, 2.5),
	},
	headerTop: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) auto auto',
		alignItems: 'start',
		columnGap: theme.spacing(2),
		rowGap: theme.spacing(1),
		[theme.breakpoints.down('xs')]: {
			gridTemplateColumns: 'minmax(0, 1fr) auto',
		},
	},
	headerTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		fontSize: '1.35rem',
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	headerIdentity: {
		minWidth: 0,
	},
	headerTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
	},
	headerSummary: {
		textAlign: 'right',
		minWidth: 250,
		[theme.breakpoints.down('xs')]: {
			gridColumn: 1,
			gridRow: 2,
			minWidth: 0,
			textAlign: 'left',
		},
	},
	headerClose: {
		gridColumn: 3,
		gridRow: 1,
		[theme.breakpoints.down('xs')]: {
			gridColumn: 2,
			gridRow: 1,
		},
	},
	amount: {
		fontSize: '1.35rem',
		fontWeight: 700,
		lineHeight: 1.2,
	},
	badges: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.75),
		marginTop: theme.spacing(1),
	},
	badge: {
		fontWeight: 600,
	},
	corporateHostBadge: {
		'&&': {
			backgroundColor: corporateBlue,
			color: '#ffffff',
		},
	},
	corporateButton: {
		'&&': {
			color: corporateBlue,
			borderColor: corporateBlue,
		},
	},
	corporateButtonContained: {
		'&&': {
			backgroundColor: corporateBlue,
			color: '#ffffff',
			'&:hover': {
				backgroundColor: '#168bdc',
			},
		},
	},
	corporateFeedback: {
		color: corporateBlue,
		fontWeight: 600,
	},
	UNINITIALIZED: uninitializedStyle,
	CREATED: pendingStyle,
	DECLINED: failureStyle,
	CANCELED: failureStyle,
	AUTHORIZED: successStyle,
	PRE_AUTHORIZED: pendingStyle,
	PRE_EDITED: pendingStyle,
	PROMPTING_USER: pendingStyle,
	MANUALLY_AUTHORIZED: successStyle,
	USERCANCELED: failureStyle,
	UNKNOWN_STATUS: pendingStyle,
	apexNotNotified: {
		backgroundColor: '#eceff3',
		color: '#5e6672',
	},
	content: {
		padding: theme.spacing(2),
		flex: '1 1 auto',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	cards: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
		gap: theme.spacing(1.5),
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	card: {
		borderRadius: 8,
		borderColor: corporateBorder,
		boxShadow: '0 1px 3px rgba(25, 45, 80, 0.08)',
		overflow: 'hidden',
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		background: corporateCyan,
		color: corporateNavy,
		fontSize: '0.9rem',
		fontWeight: 700,
	},
	fieldGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		[theme.breakpoints.down('xs')]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	operationsGrid: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr)',
	},
	field: {
		minWidth: 0,
		display: 'grid',
		gridTemplateColumns: '42% minmax(0, 1fr)',
		alignItems: 'center',
		gap: theme.spacing(1),
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(0.75, 1.5),
	},
	fieldLabel: {
		color: theme.palette.text.secondary,
		fontSize: '0.72rem',
		lineHeight: 1.25,
	},
	fieldValue: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		fontSize: '0.88rem',
		fontWeight: 500,
		lineHeight: 1.35,
		minWidth: 0,
		wordBreak: 'break-word',
		overflowWrap: 'anywhere',
	},
	fieldText: {
		minWidth: 0,
		flex: '1 1 auto',
	},
	copyButton: {
		padding: 2,
		marginTop: -3,
		color: corporateNavy,
	},
	technicalSection: {
		marginTop: theme.spacing(1.5),
	},
	exchangeGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		alignItems: 'stretch',
		gap: theme.spacing(1),
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	accordion: {
		border: `1px solid ${corporateBorder}`,
		borderRadius: '8px !important',
		boxShadow: 'none',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minWidth: 0,
		margin: '0 !important',
		'&.Mui-expanded': { margin: '0 !important' },
		'&:before': { display: 'none' },
	},
	accordionSummary: {
		minHeight: 44,
		'&$accordionExpanded': { minHeight: 44 },
		'& .MuiAccordionSummary-content': { margin: theme.spacing(0.75, 0) },
	},
	accordionExpanded: {},
	accordionDetails: {
		alignItems: 'stretch',
		display: 'flex',
		flex: '1 1 auto',
		minWidth: 0,
	},
	panelContent: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		minWidth: 0,
	},
	panelTools: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		marginLeft: 'auto',
		marginRight: theme.spacing(0.5),
	},
	code: {
		margin: 0,
		padding: theme.spacing(1.25),
		width: '100%',
		maxHeight: 260,
		overflow: 'auto',
		boxSizing: 'border-box',
		borderRadius: 4,
		background: theme.palette.type === 'dark' ? theme.palette.background.default : '#f4f6f8',
		color: theme.palette.text.primary,
		fontFamily: 'Consolas, "Courier New", monospace',
		fontSize: '0.76rem',
		lineHeight: 1.45,
		minHeight: 180,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	footer: {
		position: 'sticky',
		bottom: 0,
		zIndex: 2,
		background: theme.palette.background.paper,
		borderTop: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(1, 2),
	},
	footerActions: {
		padding: 0,
	},
}));

export default useStyles;
